/**
 * @typedef {object} ShopifyReturnConfig
 * @property {object} policy
 * @property {string[]} policy.text
 * @property {number} policy.limit
 * @property {string[]} reasonNote
 * @property {string[]} messageToCustomer
 */
import React, { Component } from 'react';
import { processMessage } from '../../../../util/dataObjectToStoreObject';
import PropTypes from 'prop-types';
import Loading from '../../../LoadingComponent/LoadingComponent';
import ImageIcon from '../../../ImageIcon/ImageIcon';
import { oneBtnPopupCreator, twoBtnPopupCreator } from '../../../popups/ConfirmPopup/helper';
import ReplacementOrderItem from '../ReplacementOrderItem/ReplacementOrderItem';
import { SHOPIFY_FINANCIAL_STATUSES } from '../../../../constants/shopifyCancelAndRefunds';
import { runSafely, messageToClientFormat } from '../../../../util';
import { DraftHelpers } from '../../../../util/Draft';
import { PolicyButton } from '../../../common/buttonsComponents';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import selectors from '../../../../selectors';
import { setSelectedTask } from '../../../../actions/tools/OrdersSearchActions';
import { shopifyReturnOrder, shopifyAddOrderNote } from '../../../../actions/tools/ShopifyActions';
import {
  extractSelectedTicketFromState,
  isSelectedTicketTaken,
  getNewMessagesEditor,
  extractSelectedTicketBusinessDetailsFromState,
} from '../../../../extractFromState';
import { setShowTools, onPasteText, saveNewMessageEditorState, addNoteToSelectedTicket } from '../../../../actions';
import {
  OrderTaskWrapper,
  OrderTaskTitle,
  BackButton,
  FlexContainer,
  FlexColumn,
  CommentTextarea,
  OrderListWrapper,
  OrderListTitle,
  OrderList,
  OrderTaskControls,
  OrderBackButton,
  OrderActionButton,
} from './ShopifyReturnOrder.styles';
import { handleError } from 'services/ErrorService/ErrorService';
import makeRequest from '../../../../api/makeRequest';
import environment from '../../../../environment';

class ShopifyReturnOrder extends Component {
  static propTypes = {
    user: PropTypes.object,
    isSelectedTicketTaken: PropTypes.bool,
    selectedShop: PropTypes.string.isRequired,
    selectedOrder: PropTypes.object.isRequired,
    selectedConfig: PropTypes.shape({
      returnConfig: PropTypes.shape({
        policy: PropTypes.shape({
          text: PropTypes.arrayOf([PropTypes.string]),
          limit: PropTypes.number,
        }).isRequired,
        reasonNote: PropTypes.arrayOf(PropTypes.string),
        messageToCustomer: PropTypes.arrayOf(PropTypes.string),
      }).isRequired,
    }).isRequired,
    selectedTicket: PropTypes.object.isRequired,
    addNoteToSelectedTicket: PropTypes.func,
    setSelectedTask: PropTypes.func,
    shopifyReturnOrder: PropTypes.func,
    shopifyAddOrderNote: PropTypes.func,
    setShowTools: PropTypes.func,
    saveNewMessageEditorState: PropTypes.func,
  };

  static defaultProps = {
    user: {},
    isSelectedTicketTaken: false,
    setShowTools: () => {},
    saveNewMessageEditorState: () => {},
    addNoteToSelectedTicket: () => Promise.resolve(),
    shopifyReturnOrder: () => Promise.resolve(),
    shopifyAddOrderNote: () => Promise.resolve(),
  };

  /**
   * @type {ShopifyReturnConfig}
   */
  defaultReturnConfig = {
    policy: {
      text: ['Returns are allowed for this partner'],
      limit: 1,
    },
    reasonNote: [''],
    messageToCustomer: ["We're sorry for the inconvenience. Your return has been processed"],
  };

  state = {
    returnOrder: {},
    returnOrderItems: {},
    isPolicyAccepted: false,
    recentlyAddedNote: undefined,
    isRequesting: false,
    reasonNote: this.getReasonNote(),
  };

  copyMacroText() {
    const selectedConversationId = this.props.selectedTicket.ticketId;
    const customMacros = this.props.selectedBusinessDetails.customMacros
      ? this.props.selectedBusinessDetails.customMacros
      : {};
    const lastMileCancellationMacro = customMacros.lastMileCancellation;
    const { ticketType, communicationType } = this.props.selectedTicket;
    const macro = messageToClientFormat(lastMileCancellationMacro, ticketType, communicationType);
    const newEditorState = DraftHelpers.setContents(macro, this.props.editorState);
    this.props.saveNewMessageEditorState(selectedConversationId, newEditorState);
    this.props.onPasteText(lastMileCancellationMacro);
  }

  /**
   * @type {ShopifyReturnConfig}
   */
  getReturnConfig() {
    return {
      ...this.defaultReturnConfig,
      ...this.props.selectedConfig.returnConfig,
    };
  }

  isOrderRefunded() {
    return this.props.selectedOrder.financial_status === SHOPIFY_FINANCIAL_STATUSES.REFUNDED;
  }

  isReturnAllow() {
    return !this.isOrderRefunded();
  }

  goBack = () => this.props.setSelectedTask(null);

  acceptPolicy = () => this.setState({ isPolicyAccepted: true });

  handlerTextInputChange = ({ target: { name, value } }) => this.setState({ [name]: value });

  getReasonNote() {
    return this.getReturnConfig()
      .reasonNote.reduce((string, chunk) => `${string}${chunk}\n`, '')
      .trim();
  }

  getMessageToCustomerText() {
    return this.getReturnConfig()
      .messageToCustomer.reduce((string, chunk) => `${string}${chunk}\n`, '')
      .trim();
  }

  get orderItems() {
    return this.props.selectedOrder.line_items;
  }

  get returnedOrderItems() {
    return this.orderItems
      .map((orderItem) => ({
        variant_id: orderItem.variant_id,
        quantity: this.state.returnOrderItems[orderItem.id],
      }))
      .filter(({ quantity }) => quantity > 0);
  }

  get returnOrderPayload() {
    return {
      shopName: this.props.selectedShop,
      simplrAccountId: this.props.selectedTicket.simplrAccountPtrId,
      expertId: this.props.user.id,
      items: this.returnedOrderItems,
    };
  }

  get addOrderNotePayload() {
    const itemObjectList = this.props.selectedOrder.line_items;
    const itemList = Object.keys(itemObjectList).map((key) => {
      const itemQty = this.state.returnOrderItems[itemObjectList[key].id];
      let result;
      if (itemQty > 0) {
        result = `ITEM ID: ${itemObjectList[key].id}, ITEM NAME: ${itemObjectList[key].name}, ITEM QTY: ${itemQty}\n`;
      } else {
        result = '';
      }

      return result;
    });
    const ticketNumber = this.props.selectedTicketNumber;

    return {
      shopName: this.props.selectedShop,
      simplrAccountId: this.props.selectedTicket.simplrAccountPtrId,
      note: `
      RETURN ORDER:
      ORDER - "${this.props.selectedOrder.name}"
      ${this.props.selectedTicket.ticketType} TICKET - "${ticketNumber}"
      ITEMS:
      ${itemList.join('\n')}
      REASON:
      ${this.state.reasonNote}`,
    };
  }

  handleReturnBtnClick = () => {
    if (this.isPayloadInvalid()) {
      return;
    }
    twoBtnPopupCreator({
      content: {
        main: 'Are you sure you want to return this order?',
        iconPath: ImageIcon.type.alertGreenBell,
      },
      btnConfig: {
        dismiss: 'DISMISS',
        confirm: 'RETURN ORDER',
      },
      onConfirm: this.returnHandler,
    });
  };

  isPayloadInvalid() {
    if (this.returnedOrderItems.length < 1) {
      oneBtnPopupCreator({
        content: {
          main: 'Please add at least one item to create return',
          iconPath: ImageIcon.type.errorEscalation,
        },
        btnConfig: 'CLOSE',
      });

      return true;
    }

    return false;
  }

  createLastMileCancellation = async () => {
    try {
      await makeRequest(`${environment.app.serverApiUrl}/v1/last-mile`, {
        method: 'post',
        data: {
          simplrAccountId: this.props.selectedBusinessDetails.id,
          conversationId: this.props.selectedTicket.ticketId,
          lastCustomerMessageId: this.state.recentlyAddedNote.messageId,
          action: 'RETURN',
        },
      });
    } catch (error) {
      handleError({ error });
      this.handleFailed(error);
    }
  };

  returnHandler = async () => {
    try {
      this.setState({ isRequesting: true });
      await this.returnOrderRequest();
      this.setState(() => ({ returnOrder: this.props.selectedOrder }));
      await runSafely(this.addNoteToConversation);
      this.handleSuccess();
      this.copyMacroText();
      await this.createLastMileCancellation();
    } catch (error) {
      this.handleFailed(error);
    } finally {
      this.setState({ isRequesting: false });
    }
  };

  returnOrderRequest = () => this.props.shopifyReturnOrder(this.props.selectedTicket.ticketId, this.returnOrderPayload);

  addNoteToConversation = async () =>
    this.setState({
      recentlyAddedNote: processMessage(await this.props.addNoteToSelectedTicket(this.addOrderNotePayload.note)),
    });

  handleSuccess = () => {
    if (this.props.isSelectedTicketTaken) {
      const { ticketType, communicationType } = this.props.selectedTicket;
      const macro = messageToClientFormat(this.getMessageToCustomerText(), ticketType, communicationType);
      const stateEditor = DraftHelpers.setContents(macro, this.props.editorState);
      this.props.saveNewMessageEditorState(this.props.selectedTicket.ticketId, stateEditor);
    }
    this.props.setSelectedTask(null);
    this.props.setShowTools(false);
    oneBtnPopupCreator({
      content: {
        main: `Successful Return for ${this.props.selectedOrder.name}`,
        iconPath: ImageIcon.type.alertGreenBell,
      },
      btnConfig: this.props.isSelectedTicketTaken ? 'REPLY TO CUSTOMER' : 'CLOSE',
    });
  };

  handleFailed = (error = {}) => {
    const { response = {} } = error;
    const { body = {} } = response;
    oneBtnPopupCreator({
      content: {
        main: body.Message || 'Return Order failed',
        iconPath: ImageIcon.type.errorEscalation,
      },
      btnConfig: 'CLOSE',
    });
  };

  checkOrderMeetsPolicy() {
    return (
      this.props.selectedOrder.financial_status === SHOPIFY_FINANCIAL_STATUSES.PARTIALLY_REFUNDED ||
      this.props.selectedOrder.financial_status === SHOPIFY_FINANCIAL_STATUSES.PAID ||
      this.props.selectedOrder.financial_status === SHOPIFY_FINANCIAL_STATUSES.PARTIALLY_PAID
    );
  }

  onQuantityChanged = (index) => (quantity) => {
    const { id } = this.orderItems[index];
    this.setState(({ returnOrderItems }) => ({
      returnOrderItems: {
        ...returnOrderItems,
        [id]: quantity,
      },
    }));
  };

  notAllowPlaceholder() {
    const content = {
      title: 'Return not allowed',
      reason: '',
    };
    if (this.isOrderRefunded) {
      content.title = 'Order has already been refunded';
      content.reason =
        'You cannot return this order because the order has already been refunded.' +
        ' Please select another action instead.';
    }

    return (
      <div className="action-unavailable-page text-center">
        <div className="flex-content">
          <div className="row">
            <div className="col-xs-12">
              <ImageIcon src={ImageIcon.type.xGreen} className="svg top-image" />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <p className="title-label">{content.title}</p>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <p className="instructions-label">{content.reason}</p>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 text-center">
              <BackButton type="button" onClick={this.goBack}>
                BACK TO ACTIONS
              </BackButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderPolicy() {
    const isMeetsPolicy = this.checkOrderMeetsPolicy();
    const imgSrc = isMeetsPolicy ? ImageIcon.type.checkmarkGreen : ImageIcon.type.xGreen;
    const alert = isMeetsPolicy ? 'This order meets the condition!' : 'This order does not meet the condition!';
    const btnAction = isMeetsPolicy ? this.acceptPolicy : this.goBack;

    return (
      <div className="policy-page text-center">
        <div className="flex-content">
          <ImageIcon src={ImageIcon.type.magnifiedDocument} className="top-image" />
          <p className="policy-title">What is the return policy?</p>
          <hr className="policy-divider" />
          <ImageIcon src={imgSrc} />
          <p className="policy-alert">{alert}</p>
          <div className="row">
            <div className="col-xs-12 text-center">
              <PolicyButton type="button" onClick={btnAction}>
                {isMeetsPolicy ? 'GO' : 'BACK TO ACTIONS'}
              </PolicyButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (!this.isReturnAllow()) {
      return this.notAllowPlaceholder();
    }
    if (!this.state.isPolicyAccepted) {
      return this.renderPolicy();
    }
    if (this.state.isRequesting) {
      return <Loading label="Send A Return..." />;
    }

    return (
      <OrderTaskWrapper>
        <OrderTaskTitle>Create a return</OrderTaskTitle>
        <OrderListWrapper className="clearfix">
          <OrderListTitle>Return Order</OrderListTitle>
          <OrderList>
            {this.orderItems.map((item, index) => (
              <ReplacementOrderItem
                key={item.id}
                order={this.props.selectedOrder}
                orderItem={item}
                quantity={this.state.returnOrderItems[item.id]}
                onQuantityChanged={this.onQuantityChanged(index)}
              />
            ))}
          </OrderList>
        </OrderListWrapper>
        <FlexContainer>
          <FlexColumn>
            <h4>Return Comment</h4>
            <CommentTextarea name="reasonNote" value={this.state.reasonNote} onChange={this.handlerTextInputChange} />
          </FlexColumn>
        </FlexContainer>
        <OrderTaskControls>
          <OrderBackButton onClick={this.goBack}>BACK</OrderBackButton>
          <OrderActionButton onClick={this.handleReturnBtnClick}>RETURN ORDER</OrderActionButton>
        </OrderTaskControls>
      </OrderTaskWrapper>
    );
  }
}

const mapStateToProps = (state) => ({
  selectedOrder: selectors.tools.getSelectedOrder(state),
  selectedConfig: selectors.tools.getSelectedShopifyConfig(state),
  selectedShop: selectors.tools.getSelectedShop(state),
  selectedTicketNumber: selectors.tools.getSelectedTicketNumber(state),
  selectedTicket: extractSelectedTicketFromState(state),
  isSelectedTicketTaken: isSelectedTicketTaken(state),
  selectedBusinessDetails: extractSelectedTicketBusinessDetailsFromState(state),
  user: state.user,
  editorState: getNewMessagesEditor(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      onPasteText,
      setSelectedTask,
      addNoteToSelectedTicket,
      setShowTools,
      saveNewMessageEditorState,
      shopifyAddOrderNote,
      shopifyReturnOrder,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ShopifyReturnOrder);
